import type Phaser from 'phaser';
import { mkCreature } from '@/domain/creature/factory';
import type { Creature } from '@/domain/creature/types';
import type { WildEnemyRuntime } from '@/domain/combat/types';
import type {
  DirectionalCreatureRendererHandle,
  DirectionalCreatureRendererOptions,
} from './DirectionalCreatureRenderer';
import { worldYSortDepth } from '@/runtime/scenes/world-visual-adapter';
import type { EnemyBehaviorState } from '@/domain/combat/enemy-behavior';
import type { ActorVisualAction } from './actor-action-state';
import { facingFromVector, type WorldFacing } from '@/domain/world/actor-presentation';

export interface WildEnemyRendererOptions extends DirectionalCreatureRendererOptions {
  reducedMotion: boolean;
  scaleMultiplier: number;
  /** Compatibilidade observável com o renderer anterior; selvagens nunca dormem. */
  sleepMode: 'awake';
  showLabels: false;
  scale: number;
}

type CreatureRendererBuilder = (
  scene: Phaser.Scene,
  creature: Creature,
  options: WildEnemyRendererOptions,
) => DirectionalCreatureRendererHandle;

export interface WildEnemyRendererHandle {
  container: Phaser.GameObjects.Container;
  rid: number;
  setPos(x: number, y: number): void;
  setHp(hp: number, max: number): void;
  setState(enemy: WildEnemyRuntime): void;
  flashHit(): void;
  destroy(): void;
}

export function wildVisualAction(
  previous: EnemyBehaviorState,
  current: EnemyBehaviorState,
): ActorVisualAction {
  if (current.kind === 'dead') return 'defeated';
  if (previous.kind === 'telegraph' && current.kind === 'recover') return 'attack';
  if (current.kind === 'chase' || current.kind === 'reposition') return 'walk';
  return 'idle';
}

export function wildEnemyAsCreature(enemy: WildEnemyRuntime): Creature {
  const genome = enemy.genome;
  const creature = mkCreature({
    genome: {
      ...genome,
      primaryTypes: [...genome.primaryTypes],
      modifiers: genome.modifiers.map((modifier) => ({ ...modifier })),
      inheritedTraits: genome.inheritedTraits.map((trait) => ({ ...trait })),
      ...(genome.recessiveGenes ? { recessiveGenes: { ...genome.recessiveGenes } } : {}),
    },
    id: `wild-${enemy.populationId}-${enemy.rid}`,
    name: `Selvagem ${enemy.populationId} #${enemy.rid}`,
    nowMs: 0,
  });
  creature.lifecycle.stage = 'adult';
  creature.lifecycle.bornAt = -10_000_000;
  creature.state.hunger = 100;
  creature.state.happiness = 100;
  creature.state.vitality = 100;
  creature.state.sick = false;
  creature.state.dead = false;
  return creature;
}

export function wildEnemyRendererOptions(enemy: WildEnemyRuntime, reducedMotion = false): WildEnemyRendererOptions {
  const scaleMultiplier = (enemy.rank === 'dominant' ? 1.18 : 0.92) * enemy.scale;
  return {
    reducedMotion,
    scaleMultiplier,
    scale: scaleMultiplier,
    showLabels: false,
    sleepMode: 'awake',
  };
}

export function buildWildEnemyRenderer(
  scene: Phaser.Scene,
  enemy: WildEnemyRuntime,
  reducedMotion: boolean,
  buildCreature: CreatureRendererBuilder,
): WildEnemyRendererHandle {
  const creature = wildEnemyAsCreature(enemy);
  const renderer = buildCreature(scene, creature, wildEnemyRendererOptions(enemy, reducedMotion));
  renderer.container.setPosition(enemy.x, enemy.y).setDepth(worldYSortDepth(enemy.y).object);
  let previousBehavior = enemy.behavior;
  let lastFacing: WorldFacing = 'left';

  const accent = scene.add.graphics();
  drawBiomeAccent(accent, enemy);
  renderer.container.add(accent);
  const hpBg = scene.add.rectangle(0, -19, 18, 2, 0x000000, 0.75).setOrigin(0.5);
  const hpFg = scene.add.rectangle(0, -19, 18, 2, 0xff5566).setOrigin(0.5);
  renderer.container.add([hpBg, hpFg]);

  function setHp(hp: number, max: number): void {
    const pct = Math.max(0, Math.min(1, hp / Math.max(1, max)));
    hpFg.scaleX = pct;
    hpFg.setVisible(hp < max);
    hpBg.setVisible(hp < max);
  }
  function setState(current: WildEnemyRuntime): void {
    const action = wildVisualAction(previousBehavior, current.behavior);
    if (action === 'attack') renderer.playAttack(lastFacing);
    else if (action === 'defeated') renderer.playDefeated(lastFacing);
    previousBehavior = current.behavior;
    renderer.container.setAlpha(current.behavior.kind === 'recover' ? 0.78 : 1);
  }
  return {
    container: renderer.container,
    rid: enemy.rid,
    setPos(x, y) {
      const dx = x - renderer.container.x;
      const dy = y - renderer.container.y;
      lastFacing = facingFromVector(dx, dy, lastFacing);
      renderer.container.setPosition(Math.round(x), Math.round(y));
      renderer.setMovement(dx, dy, 16);
      renderer.setWorldDepth(worldYSortDepth(y).object);
    },
    setHp,
    setState,
    flashHit() {
      renderer.playHurt(lastFacing);
      scene.tweens.add({ targets: renderer.container, alpha: 0.35, duration: 80, yoyo: true });
    },
    destroy() {
      scene.tweens.killTweensOf(renderer.container);
      renderer.destroy();
    },
  };
}

function drawBiomeAccent(graphics: Phaser.GameObjects.Graphics, enemy: WildEnemyRuntime): void {
  if (enemy.rank === 'dominant') {
    graphics.lineStyle(1, 0xffe08a, 0.95).strokeCircle(0, 0, 18);
  }
  if (enemy.biome === 'meadow') {
    graphics.fillStyle(0x7ee787, 1).fillCircle(-12, 11, 2).fillStyle(0xffd166, 1).fillCircle(-12, 9, 1);
  } else if (enemy.biome === 'forest') {
    graphics.fillStyle(0x4f9d69, 0.9).fillRect(-14, 10, 7, 2);
  } else if (enemy.biome === 'cave') {
    graphics.fillStyle(0x77ddff, 0.95).fillTriangle(11, 12, 14, 5, 16, 12);
  } else {
    graphics.lineStyle(1, 0xeefaff, 0.9).arc(0, 13, 12, Math.PI, Math.PI * 2);
  }
}
